<template>
  <b-card title="Aikajana" class="mt-2">
    <div style="max-height: 40vh; overflow: scroll">
      <app-timeline v-for="item in applicationUpdates" :key="item.id">
        <app-timeline-item
          :badge="
            item.state === 'request_documents' && applicationNotifications
              ? applicationNotifications
              : 0
          "
          :badge-classes="
            item.state === 'request_documents' && applicationNotifications ? 'bg-primary' : ''
          "
          :icon="getIcon(item.state)"
          :variant="getVariant(item.state)"
          style="margin-bottom: 20px; margin-top: 10px"
        >
          <div
            class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0"
          >
            <h6>{{ getState(item.state) }}</h6>
            <small class="text-muted">{{
              item.date.toDate().toLocaleString('fi', { month: 'numeric', day: 'numeric' })
            }}</small>
          </div>
          <p v-if="item.content.message">
            {{ item.content.message }}
          </p>
          <b-button
            v-if="item.state === 'request_documents'"
            size="sm"
            variant="outline-warning"
            style="margin-bottom: 4px"
            :href="`https://hoiwa.zendesk.com/agent/tickets/${item.content.message.split(':')[0]}`"
            target="_blank"
          >
            Aukaise pyyntö
          </b-button>
          <b-button
            v-if="item.state === 'send_message'"
            size="sm"
            variant="outline-info"
            style="margin-bottom: 4px"
            :href="`https://hoiwa.zendesk.com/agent/tickets/${item.content.message}`"
            target="_blank"
          >
            Aukaise viesti
          </b-button>
          <b-media>
            <template #aside>
              <b-avatar
                size="22px"
                variant="light-info"
                :title="item.processer.firstName + ' ' + item.processer.lastName"
                :text="item.processer.firstName.charAt(0) + ' ' + item.processer.lastName.charAt(0)"
              />
            </template>
            <span
              class="text-muted"
              style="text-transform: capitalize;"
            >
              {{ item.processer.firstName }} {{ item.processer.lastName }}
            </span
            >
          </b-media>
        </app-timeline-item>
      </app-timeline>
    </div>
  </b-card>
</template>

<script>
import { BCard, BAvatar, BButton, BMedia } from 'bootstrap-vue'
import { avatarText } from '@hw-ui/utils/filter'
import AppTimeline from '@hw-ui/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@hw-ui/components/app-timeline/AppTimelineItem.vue'
import useApplicationsList from '../applications-list/useApplicationsList'

export default {
  components: {
    BCard,
    BButton,
    BAvatar,
    BMedia,
    AppTimelineItem,
    AppTimeline,
  },
  props: {
    applicationUpdates: {
      type: Array,
      required: true,
    },
    applicationNotifications: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      fields: [
        { key: 'state', label: 'Status' },
        { key: 'date', label: 'Date' },
        { key: 'duration', label: 'Duration' },
        { key: 'processer', label: 'Processer' },
        { key: 'content', label: 'Message' },
      ],
    }
  },
  methods: {
    toDate(date) {
      if (date instanceof Date) {
        return date.toLocaleString('fi')
      }
      return date.toDate().toLocaleString('fi')
    },
    toMinutes(time) {
      const minutes = Math.floor(time / 60)
      const seconds = time - minutes * 60
      return `${minutes}m ${seconds}s`
    },
    getVariant(state) {
      let result = ''
      switch (state) {
        case 'accepted':
          result = 'success'
          break
        case 'confirm_valvira':
          result = 'primary'
          break
        case 'no_valvira':
          result = 'danger'
          break
        case 'rejected':
          result = 'danger'
          break
        case 'schedule':
          result = 'warning'
          break
        case 'no_answer':
          result = 'secondary'
          break
        case 'send_interview':
          result = 'info'
          break
        case 'confirm_interview':
          result = 'primary'
          break
        case 'request_documents':
          result = 'warning'
          break
        case 'submit_interview':
          result = 'primary'
          break
        case 'confirmed_documents':
          result = 'primary'
          break
        case 'send_sms':
          result = 'info'
          break
        case 'send_message':
          result = 'info'
          break
        case 'contract_sign_request':
          result = 'info'
          break
        case 'contract_signed':
          result = 'success'
          break
        case 'change_status_love':
          result = 'warning'
          break
        case 'remove_documents':
          result = 'warning'
          break
        default:
          result = 'info'
      }
      return result
    },
    getIcon(state) {
      let result = ''
      switch (state) {
        case 'accepted':
          result = 'ThumbsUpIcon'
          break
        case 'confirm_valvira':
          result = 'UserCheckIcon'
          break
        case 'no_valvira':
          result = 'UserMinusIcon'
          break
        case 'rejected':
          result = 'ThumbsDownIcon'
          break
        case 'schedule':
          result = 'CalendarIcon'
          break
        case 'no_answer':
          result = 'PhoneOffIcon'
          break
        case 'send_interview':
          result = 'SendIcon'
          break
        case 'submit_interview':
          result = 'MessageSquareIcon'
          break
        case 'request_documents':
          result = 'FileTextIcon'
          break
        case 'confirmed_documents':
          result = 'MailIcon'
          break
        case 'confirm_interview':
          result = 'CheckSquareIcon'
          break
        case 'send_sms':
          result = 'MessageCircleIcon'
          break
        case 'send_message':
          result = 'InboxIcon'
          break
        case 'contract_sign_request':
          result = 'ShareIcon'
          break
        case 'contract_signed':
          result = 'Edit3Icon'
          break
        case 'change_status_love':
          result = 'FileTextIcon'
          break
        case 'remove_documents':
          result = 'FileMinusIcon'
          break
        default:
          result = 'ArrowRightIcon'
      }
      return result
    },
    getState(state) {
      let result = ''
      switch (state) {
        case 'accepted':
          result = 'Hyväksytty'
          break
        case 'confirm_valvira':
          result = this.$t('application.steps.valviraConfirmed')
          break
        case 'no_valvira':
          result = this.$t('application.steps.noValvira')
          break
        case 'rejected':
          result = 'Hylätty'
          break
        case 'schedule':
          result = this.$t('application.steps.interviewScheduled')
          break
        case 'no_answer':
          result = this.$t('application.steps.noAnswer')
          break
        case 'send_interview':
          result = this.$t('application.steps.interviewSent')
          break
        case 'submit_interview':
          result = this.$t('application.steps.interviewSubmitted')
          break
        case 'request_documents':
          result = this.$t('application.steps.requestDocuments')
          break
        case 'confirmed_documents':
          result = this.$t('application.steps.confirmedDocuments')
          break
        case 'confirm_interview':
          result = this.$t('application.steps.interviewConfirmed')
          break
        case 'send_sms':
          result = 'Send SMS'
          break
        case 'send_message':
          result = 'Sposti lähetetty'
          break
        case 'contract_sign_request':
          result = this.$t('application.steps.interviewConfirmed')
          break
        case 'contract_signed':
          result = this.$t('application.steps.contractSigned')
          break
        case 'change_status_love':
          result = this.$t('application.steps.changeLove')
          break
        case 'remove_documents':
          result = this.$t('application.steps.removeDocument')
          break
        default:
          result = this.$t('application.steps.applied')
      }
      return result
    },
    getMessageString(message, state) {
      let formattedMessage = message
      if (state === 'request_documents') {
        formattedMessage = `#${message}`
      }
      return formattedMessage
    },
  },
  setup() {
    const { resolveUserRoleVariant } = useApplicationsList()
    return {
      avatarText,
      resolveUserRoleVariant,
    }
  },
}
</script>

<style lang="scss" scoped>
.timeline-item {
  // This gives shade to last timeline-item but isn't that revolutionary
  &:last-of-type {
    &:after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 1px;
      height: 200%;
      background: #ebe9f1;
    }
  }
}
</style>
